import React, { MouseEvent } from 'react';
import { IconButton, ListItem, styled, Typography } from '@mui/material';
import { MoreHoriz } from '@mui/icons-material';
import { useGuide } from 'pages/guide/hooks/useGuide';
import { Guide } from 'types/guide';

interface GuideItemModuleProps {
  guide: Guide;
}

const ListItemStyled = styled(ListItem)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
}));

const Header = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  gap: theme.spacing(1),
  alignItems: 'center',
}));

const HeaderInfo = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  minWidth: 0,
  flexWrap: 'wrap',
}));

const GuideId = styled(Typography)(({ theme }) => ({
  display: 'inline-flex',
  width: 'fit-content',
  maxWidth: '100%',
  marginBottom: theme.spacing(0.5),
  padding: '2px 6px',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.grey[100],
  color: theme.palette.text.secondary,
  fontSize: 12,
  fontWeight: 700,
  lineHeight: 1.2,
}));

const FullInfo = styled('div')({
  minWidth: 0,
});

const ProcedureTitle = styled(Typography)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  lineClamp: 2,
  WebkitBoxOrient: 'vertical',
});

const Actions = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: theme.spacing(0.25),
  whiteSpace: 'nowrap',
}));

const Status = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  width: 'fit-content',
  maxWidth: '100%',
  padding: '4px 8px',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.grey[200],
  color: theme.palette.text.primary,
  fontSize: 12,
  fontWeight: 700,
  lineHeight: 1.2,
  '&.scheduled': {
    backgroundColor: theme.palette.info.main,
    color: theme.palette.info.contrastText,
  },
  '&.completed': {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
  },
  '&.deleted': {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
}));

const Content = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: theme.spacing(1.25),
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

const Values = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  gap: theme.spacing(1),
  paddingTop: theme.spacing(1),
  borderTop: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: '1fr 1fr',
  },
}));

const Field = styled('div')({
  minWidth: 0,
});

const Description = styled(Typography)({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 1,
  lineClamp: 1,
  WebkitBoxOrient: 'vertical',
});

const GuideItemModule: React.FC<GuideItemModuleProps> = ({ guide }) => {
  const { setAnchorEl, setSelectedGuide } = useGuide();

  function handleActionClick(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setSelectedGuide(guide);
  }

  return (
    <ListItemStyled>
      <Header>
        <HeaderInfo>
          <GuideId>Guia #{guide.id}</GuideId>
          <Status className={guide.status}>{guide.formattedStatus}</Status>
        </HeaderInfo>

        <Actions>
          <IconButton size="small" color="default" onClick={handleActionClick}>
            <MoreHoriz fontSize="small" />
          </IconButton>
        </Actions>
      </Header>

      <FullInfo>
        <ProcedureTitle variant="subtitle2" fontWeight={700}>
          {guide.formattedProcedureName}
        </ProcedureTitle>
        <Typography variant="caption" color="textSecondary">
          Consulta em {guide.formattedAppointmentDate}
        </Typography>
      </FullInfo>

      <Content>
        <Field>
          <Typography variant="caption" color="textSecondary">
            Paciente
          </Typography>
          <Description variant="body2">{guide.formattedPatientName}</Description>
        </Field>

        <Field>
          <Typography variant="caption" color="textSecondary">
            Parceiro
          </Typography>
          <Description variant="body2">{guide.formattedDoctorName}</Description>
        </Field>

        <Field>
          <Typography variant="caption" color="textSecondary">
            Marcador
          </Typography>
          <Description variant="body2">{guide.formattedMarkerName}</Description>
        </Field>

        <Field>
          <Typography variant="caption" color="textSecondary">
            Procedimento
          </Typography>
          <Description variant="body2">{guide.formattedProcedureName}</Description>
        </Field>
      </Content>

      <Values>
        <Field>
          <Typography variant="caption" color="textSecondary">
            Vl. Marcador
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {guide.formattedValueMarker}
          </Typography>
        </Field>

        <Field>
          <Typography variant="caption" color="textSecondary">
            Vl. Proc.
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {guide.formattedValueProcedure}
          </Typography>
        </Field>

        <Field>
          <Typography variant="caption" color="textSecondary">
            Vl. Desc.
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {guide.formattedValueDiscount}
          </Typography>
        </Field>

        <Field>
          <Typography variant="caption" color="textSecondary">
            Vl. Total
          </Typography>
          <Typography variant="body2" fontWeight={700} color="primary">
            {guide.formattedValueTotal}
          </Typography>
        </Field>
      </Values>
    </ListItemStyled>
  );
};

export default GuideItemModule;
